import { SyntheticEvent, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { IChangePasswordForm } from '@/features/dashboard-users/store/types/password.types';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import usePasswordRules from '@/features/dashboard-users/hooks/changePassword/usePasswordRules';

export function useChangePasswordForm() {
  const config = useDashboardConfig();

  const defaultValues = {
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    ...(config?.api.encryption && { passphrase: '', repeatPassphrase: '' }),
  };

  const { watch, setValue } = useForm<IChangePasswordForm>({ defaultValues });

  const handleInputChange = useCallback(
    (field: keyof IChangePasswordForm, event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.currentTarget.value;
      setValue(field, value);
    },
    [setValue],
  );

  const currentPasswordWatch = watch('currentPassword');
  const newPasswordWatch = watch('newPassword');
  const confirmNewPasswordWatch = watch('confirmNewPassword');

  const passphraseWatch = watch('passphrase');
  const repeatPassphraseWatch = watch('repeatPassphrase');

  const {
    isCaracterNumberValid,
    isPasswordContainsLowercase,
    isPasswordContainsUppercase,
    isPasswordContainsNumber,
    isPasswordContainsSpecialChar,
  } = usePasswordRules(newPasswordWatch);

  const isNewAndConfirmPasswordTheSame = useMemo(
    () => newPasswordWatch === confirmNewPasswordWatch,
    [confirmNewPasswordWatch, newPasswordWatch],
  );

  const isNewPasswordDifferentFromCurrent = useMemo(
    () => currentPasswordWatch !== newPasswordWatch,
    [currentPasswordWatch, newPasswordWatch],
  );

  const isNewPasswordValid = useMemo(
    () =>
      isCaracterNumberValid &&
      isPasswordContainsLowercase &&
      isPasswordContainsUppercase &&
      isPasswordContainsNumber &&
      isPasswordContainsSpecialChar,
    [
      isCaracterNumberValid,
      isPasswordContainsLowercase,
      isPasswordContainsUppercase,
      isPasswordContainsNumber,
      isPasswordContainsSpecialChar,
    ],
  );

  const isFormValid = useMemo(
    () => isNewPasswordValid && isNewAndConfirmPasswordTheSame && isNewPasswordDifferentFromCurrent,
    [isNewPasswordValid, isNewAndConfirmPasswordTheSame, isNewPasswordDifferentFromCurrent],
  );

  return {
    config,
    currentPasswordWatch,
    newPasswordWatch,
    confirmNewPasswordWatch,
    isNewAndConfirmPasswordTheSame,
    isCaracterNumberValid,
    isPasswordContainsLowercase,
    isPasswordContainsUppercase,
    isPasswordContainsSpecialChar,
    isFormValid,
    isPasswordContainsNumber,
    isNewPasswordDifferentFromCurrent,
    isNewPasswordValid,
    passphraseWatch,
    repeatPassphraseWatch,
    handleInputChange,
  };
}
